import { serverUnavailable, markServerUnavailable } from '@/vendor/serverAvailability'
import { sendError, sendInfo } from '@/utils/sendError'
import { isOffline } from '@/utils/networkStatus'
import { isConnected } from '@/utils/connectivity'
import { isAppInForeground } from '@/utils/appState'

const MAX_RETRIES = 3
const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 8000

type FetchFn = typeof fetch

let originalFetch: FetchFn | null = null

export function getOriginalFetch(): FetchFn {
  return originalFetch ?? globalThis.fetch.bind(globalThis)
}

const RETRYABLE_MESSAGES = [
  'failed to fetch',
  'network',
  'unable to resolve host',
  'no address associated with hostname',
  'dns',
  'enotfound',
  'econnrefused',
  'econnreset',
  'etimedout',
  'timeout',
]

const RETRYABLE_ERROR_NAMES = ['TypeError', 'AbortError']

const RETRYABLE_STATUSES = [502, 503, 504]

function isRetryableError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const msg = error.message.toLowerCase()
  return (
    RETRYABLE_MESSAGES.some((pattern) => msg.includes(pattern)) ||
    RETRYABLE_ERROR_NAMES.includes(error.name)
  )
}

function isRetryableStatus(status: number): boolean {
  return RETRYABLE_STATUSES.includes(status)
}

function getDelayMs(attempt: number): number {
  const exponential = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS)
  const jitter = Math.random() * 0.3 * exponential
  return exponential + jitter
}

function getUrlString(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

async function fetchWithRetry(
  fetchFn: FetchFn,
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let lastError: unknown

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetchFn(input, init)

      if (isRetryableStatus(response.status)) {
        // Marking flips connectivity to "disconnected"; from here the recovery
        // poller (not per-request retries) owns getting back online, so we don't
        // hammer the server. Hand the 5xx back instead of looping.
        markServerUnavailable()
        if (attempt < MAX_RETRIES && isConnected() && isAppInForeground()) {
          sendInfo('retry: fetch-client retryable status', {
            source: 'fetch-client',
            url: getUrlString(input),
            attempt: String(attempt + 1),
            status: String(response.status),
          })
          await new Promise((r) => setTimeout(r, getDelayMs(attempt)))
          continue
        }
        return response
      }

      serverUnavailable.value = false
      return response
    } catch (error) {
      lastError = error

      // Device is offline. Retrying can't succeed, and this is an expected
      // condition, not a bug — fail fast without reporting.
      if (isOffline()) {
        serverUnavailable.value = false
        throw error
      }

      if (!isRetryableError(error)) {
        serverUnavailable.value = false
        sendError(error, {
          source: 'fetch-client',
          reason: 'non-retryable',
          url: getUrlString(input),
          errorName: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : String(error),
        })
        throw error
      }

      // A retryable transport error means the server is unreachable. Mark it (which
      // disconnects us) and let the recovery poller take over rather than retrying
      // in a tight loop across every failing request.
      markServerUnavailable()
      if (attempt < MAX_RETRIES && isConnected() && isAppInForeground()) {
        sendInfo('retry: fetch-client retryable error', {
          source: 'fetch-client',
          url: getUrlString(input),
          attempt: String(attempt + 1),
          errorName: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : String(error),
        })
        await new Promise((r) => setTimeout(r, getDelayMs(attempt)))
        continue
      }
      sendError(error, {
        source: 'fetch-client',
        reason: 'server-unreachable',
        url: getUrlString(input),
        attempts: String(attempt + 1),
        errorName: error instanceof Error ? error.name : 'unknown',
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  serverUnavailable.value = false
  throw lastError
}

export function patchGlobalFetch(): void {
  if (originalFetch !== null) return

  originalFetch = globalThis.fetch.bind(globalThis)

  globalThis.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    return fetchWithRetry(originalFetch!, input, init)
  }
}
